import React, { useState, useCallback, useMemo } from 'react';
import Button from '../ui/Button';
import { formatFullDateTime } from '../../utils/dateUtils';

const InterviewCancelModal = ({ interview, onClose, onCancel }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onCancel(interview.id, reason);
      onClose();
    } finally {
      setLoading(false);
    }
  }, [interview?.id, reason, onCancel, onClose]);

  const formattedDateTime = useMemo(() => {
    return interview ? formatFullDateTime(interview.scheduledTime) : '';
  }, [interview]);

  if (!interview) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Cancel Interview</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="text-gray-400 text-xl">×</span>
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Interview Info */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-red-600 text-xl">⚠️</span>
              <h3 className="font-medium text-red-900">You are about to cancel this interview</h3>
            </div>
            <div className="text-sm text-red-800">
              <p className="font-medium">{interview.jobTitle}</p>
              <p>Scheduled for: {formattedDateTime}</p>
              <p>With: {interview.employerName}</p>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>Please note:</strong> Cancelling this interview may affect your application status 
              and future opportunities with this employer. Consider rescheduling instead if possible.
            </p>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Cancellation (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please provide a brief reason for cancelling..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Providing a reason helps maintain good communication with the employer.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Keep Interview
            </Button>
            <Button
              type="submit"
              variant="outline"
              className="bg-red-600 hover:bg-red-700 hover:text-white focus:ring-red-500"
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Cancelling...' : 'Cancel Interview'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterviewCancelModal;
